// Appointments tab — THE appointment screen. Upcoming on top, then the history
// grouped by service type with last/next per service; filter by service and by
// tech. Every row expands in place to the full record (AppointmentRecord): the
// standalone page is gone as a destination, and dispatch's card opens this tab.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import api from "../../../apiClient";
import { listOperationalCustomerAppointments } from "../../../api/operationsCustomersApi";
import { getIndustryKey } from "../../../constants/uiRegistry";
import { clean, coverageText, dateTime, plainWords, shortDate } from "../customerFormat";
import { Select } from "../../ui/input";
import Button from "../../ui/button";
import Badge from "../../ui/badge";
import AppointmentRecord from "../../Schedule/AppointmentRecord";
import ScheduleComposer from "./ScheduleComposer";
import { frIdOf, TabNote, TabState } from "./tabShared";

const PAGE = 50;
const toRows = (v) => (Array.isArray(v) ? v : Array.isArray(v?.results) ? v.results : []);

// One visit row, whichever system it came from. `detailId` is what the record
// endpoint resolves: the CRM uuid, or the FieldRoutes external id.
function normalizeFr(a) {
  const cancelled = Boolean(a.cancelled_at);
  const status = cancelled ? "Cancelled" : a.completed_at ? "Completed" : plainWords(a.status) || "Scheduled";
  return {
    key: `fr-${a.external_id}`,
    detailId: String(a.external_id),
    when: a.completed_at || a.scheduled_start,
    service: clean(a.service_name) || clean(a.service_type) || "Service visit",
    tech: clean(a.serviced_by_name) || clean(a.technician_name),
    status,
    duration: a.duration_minutes || null,
    notes: clean(a.notes),
    source: `FR #${a.external_id}`,
    fr: true,
  };
}

function normalizeCrm(a) {
  return {
    key: `crm-${a.id}`,
    detailId: String(a.id),
    when: a.start_time || a.created_at,
    service: clean(a.service_type_name) || clean(a.service_type) || "Appointment",
    tech: clean(a.assigned_to_name) || clean(a.technician_name) || "",
    status: plainWords(a.status) || "Scheduled",
    duration: a.duration_minutes || null,
    notes: clean(a.notes),
    source: "Abon",
    fr: false,
  };
}

const tone = (status) => (status === "Completed" ? "success" : status === "Cancelled" ? "neutral" : /no.?show|missed/i.test(status) ? "warning" : "accent");

export default function AppointmentsTab({ record, focusAppointment = null, boardStartLabel = null }) {
  const raw = record?.raw || {};
  const frId = frIdOf(record);
  const cov = raw.fieldroutes?.appointments || null;
  const customerPk = raw.id;

  const [frRows, setFrRows] = useState([]);
  const [frNext, setFrNext] = useState(null);
  const [crmRows, setCrmRows] = useState([]);
  const [state, setState] = useState("loading");
  const [loadingMore, setLoadingMore] = useState(false);
  const [open, setOpen] = useState(() => new Set(focusAppointment ? [String(focusAppointment)] : []));
  const [serviceFilter, setServiceFilter] = useState("");
  const [techFilter, setTechFilter] = useState("");
  const focusRef = useRef(null);

  const industryKey = getIndustryKey("general");
  const canScheduleHere = industryKey !== "pest_control";
  const [scheduling, setScheduling] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const loadFr = useCallback(
    async (page) => {
      const { data } = await listOperationalCustomerAppointments(frId, { page, page_size: PAGE });
      return { rows: toRows(data), hasNext: Boolean(data?.next) };
    },
    [frId]
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      setState("loading");
      try {
        const [fr, crm] = await Promise.all([
          frId ? loadFr(1) : Promise.resolve({ rows: [], hasNext: false }),
          customerPk
            ? api.get("/appointments/appointments/", { params: { customer_id: customerPk } }).then((r) => toRows(r.data))
            : Promise.resolve([]),
        ]);
        if (!alive) return;
        setFrRows(fr.rows);
        setFrNext(fr.hasNext ? 2 : null);
        setCrmRows(frId ? crm.filter((a) => !a?.metadata?.fieldroutes_appointment_id) : crm);
        setState("ok");
      } catch {
        if (alive) setState("error");
      }
    })();
    return () => { alive = false; };
  }, [frId, customerPk, loadFr, reloadKey]);

  // The visit dispatch opened: expanded and scrolled into view once the list is up.
  useEffect(() => {
    if (state !== "ok" || !focusAppointment) return;
    const t = setTimeout(() => focusRef.current?.scrollIntoView({ block: "start", behavior: "smooth" }), 120);
    return () => clearTimeout(t);
  }, [state, focusAppointment]);

  const more = async () => {
    if (!frNext) return;
    setLoadingMore(true);
    try {
      const next = await loadFr(frNext);
      setFrRows((rows) => [...rows, ...next.rows]);
      setFrNext(next.hasNext ? frNext + 1 : null);
    } finally {
      setLoadingMore(false);
    }
  };

  const all = useMemo(() => [...frRows.map(normalizeFr), ...crmRows.map(normalizeCrm)], [frRows, crmRows]);
  const services = useMemo(() => [...new Set(all.map((r) => r.service).filter(Boolean))].sort(), [all]);
  const techs = useMemo(() => [...new Set(all.map((r) => r.tech).filter(Boolean))].sort(), [all]);

  const { upcoming, groups, total } = useMemo(() => {
    const now = Date.now();
    const rows = all.filter((r) => (!serviceFilter || r.service === serviceFilter) && (!techFilter || r.tech === techFilter));
    const up = [];
    const done = [];
    rows.forEach((r) => {
      const t = new Date(r.when || 0).getTime();
      if (t >= now && r.status !== "Completed" && r.status !== "Cancelled") up.push(r);
      else done.push(r);
    });
    up.sort((a, b) => new Date(a.when) - new Date(b.when));
    done.sort((a, b) => new Date(b.when) - new Date(a.when));
    // History grouped by service type; each group knows its last visit and the next one booked.
    const byService = new Map();
    done.forEach((r) => {
      if (!byService.has(r.service)) byService.set(r.service, { service: r.service, rows: [], last: null, next: null });
      const g = byService.get(r.service);
      g.rows.push(r);
      if (!g.last && r.status === "Completed") g.last = r;
    });
    up.forEach((r) => {
      const g = byService.get(r.service);
      if (g && (!g.next || new Date(r.when) < new Date(g.next.when))) g.next = r;
    });
    const list = [...byService.values()].sort((a, b) => new Date(b.rows[0]?.when || 0) - new Date(a.rows[0]?.when || 0));
    return { upcoming: up, groups: list, total: rows.length };
  }, [all, serviceFilter, techFilter]);

  const toggle = (id) => setOpen((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const header = (
    <div className="apt-bar">
      {services.length > 1 ? (
        <Select value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)} aria-label="Filter by service" className="apt-filter">
          <option value="">All services</option>
          {services.map((s) => <option key={s} value={s}>{s}</option>)}
        </Select>
      ) : null}
      {techs.length > 1 ? (
        <Select value={techFilter} onChange={(e) => setTechFilter(e.target.value)} aria-label="Filter by technician" className="apt-filter">
          <option value="">All techs</option>
          {techs.map((t) => <option key={t} value={t}>{t}</option>)}
        </Select>
      ) : null}
      {canScheduleHere ? (
        <Button size="sm" variant="secondary" className="apt-schedule" onClick={() => setScheduling(true)}>
          {industryKey === "events" ? "Book the space" : "Schedule visits"}
        </Button>
      ) : null}
    </div>
  );

  if (scheduling) {
    return (
      <div className="ct-stack">
        <ScheduleComposer customer={record} industry={industryKey} onScheduled={() => setReloadKey((k) => k + 1)} onCancel={() => setScheduling(false)} />
      </div>
    );
  }
  if (state === "loading") return <div className="ct-stack">{header}<TabState>Loading appointments…</TabState></div>;
  if (state === "error") return <div className="ct-stack">{header}<TabState>Couldn't load appointments. Try again in a moment.</TabState></div>;
  if (!all.length) {
    return (
      <div className="ct-stack">
        {header}
        <TabState>{cov ? coverageText(cov, "appointments") : "No appointments on record for this customer."}</TabState>
      </div>
    );
  }

  const renderRow = (r) => {
    const isOpen = open.has(r.detailId);
    const focused = focusAppointment && String(focusAppointment) === r.detailId;
    return (
      <div key={r.key} className={`apt-row${isOpen ? " is-open" : ""}`} ref={focused ? focusRef : undefined} data-appointment={r.detailId}>
        <button type="button" className="apt-row__head" onClick={() => toggle(r.detailId)} aria-expanded={isOpen}>
          <span className="apt-row__chev" aria-hidden="true">{isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</span>
          <span className="apt-row__when">{dateTime(r.when)}</span>
          <span className="apt-row__main">
            <span className="apt-row__service">{r.service}</span>
            <span className="apt-row__meta">{[r.tech, r.duration ? `${r.duration} min` : "", r.source].filter(Boolean).join(" · ")}</span>
          </span>
          <Badge tone={tone(r.status)}>{r.status}</Badge>
        </button>
        {isOpen ? (
          <div className="apt-row__body">
            <AppointmentRecord id={r.detailId} boardStartLabel={focused ? boardStartLabel : null} onChanged={() => setReloadKey((k) => k + 1)} />
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="ct-stack apt">
      {header}
      {cov && cov.state === "partial" ? <TabNote>{coverageText(cov, "appointments")} from FieldRoutes.</TabNote> : null}

      <section className="cov-card">
        <div className="cov-card__head"><span>Upcoming</span><span className="cov-card__count">{upcoming.length}</span></div>
        <div className="cov-card__body apt-list">
          {upcoming.length ? upcoming.map(renderRow) : <div className="cov-empty">Nothing scheduled{serviceFilter || techFilter ? " for this filter" : ""}.</div>}
        </div>
      </section>

      {groups.map((g) => (
        <section key={g.service} className="cov-card">
          <div className="cov-card__head apt-group__head">
            <span>{g.service}</span>
            <span className="apt-group__facts">
              {g.last ? `Last ${shortDate(g.last.when)}${g.last.tech ? ` · ${g.last.tech}` : ""}` : "No completed visit"}
              {" · "}
              {g.next ? `Next ${shortDate(g.next.when)}` : "Nothing booked"}
            </span>
            <span className="cov-card__count">{g.rows.length}</span>
          </div>
          <div className="cov-card__body apt-list">{g.rows.map(renderRow)}</div>
        </section>
      ))}
      {!groups.length && total === upcoming.length ? null : null}
      {frNext ? (
        <Button variant="secondary" size="sm" onClick={more} disabled={loadingMore}>{loadingMore ? "Loading…" : "Show older visits"}</Button>
      ) : null}
    </div>
  );
}
